import axios from 'axios';
import { v4 as uuid } from 'uuid';
import residentialComplexTemplate from '../configuration-templates/residential_complex.json'
import residentialHouseholdTemplate from '../configuration-templates/residential_household.json'
import publicParkingTemplate from '../configuration-templates/public_parking.json'
import companyTemplate from '../configuration-templates/company.json'
import industryTemplate from '../configuration-templates/industry.json'
import buildDefaultDevices from '../shared/build_default_devices.js'

const HTTP = axios.create({
    baseURL: '/api/v1/'
});

export default {
    // request site info and status
    async requestSite({commit}, siteId) {
        return HTTP.get(`sites/${siteId}.json?include_status=true`).then(resp => commit('setSite', resp.data))
    },

    // request manufacturers and all product model version meta data
    async requestManufacturersAndProductModelVersions({commit}) {
        return HTTP.get(`manufacturers/product_model_versions.json`).then(resp => commit('setManufacturersAndProductModels', resp.data))
    },

    // request manufacturers and all extension models
    async requestExtensionModels({commit}) {
        return HTTP.get(`manufacturers/extension_models.json`).then(resp => commit('setManufacturersAndExtensionModels', resp.data))
    },

    //request current applied configurations on edge-device
    async requestAndSaveLinkConfigurations({commit}, edgeDeviceId) {
        return HTTP.get(`edge-devices/${edgeDeviceId}/configurations`).then(resp => commit('setCurrentLinkConfigurations', resp.data))
    },

    // request supported product ids (models / versions)
    async requestSupportedProducts({commit, state}) {
        return HTTP.get(`sites/${state.site.id}/configuration/products`)
            .then(resp => commit('setSupportedProductsIds', resp.data))
    },

    // request meta data for a specific device model or device model version
    // identified by a product id
    requestProductMetaData({commit, state}, productId) {
        commit('startLoading')

        return HTTP.get(`sites/${state.site.id}/configuration/product?product_id=${productId}`)
            .then(resp => commit('setProductMetaData', resp.data))
            .finally(commit('stopLoading'))
    },

    // request product config for a specific device specification
    async requestDeviceSpecificationConfig({commit, state}, {deviceTemplateId, configId}) {
        commit('startLoading')

        return HTTP.get(`sites/${state.site.id}/configuration/product?product_id=${configId}`)
            .then(resp => commit('setDeviceTemplateThings', { deviceTemplateId, configId, data: resp.data }))
            .finally(commit('stopLoading'))
    },

    // request protocol specification for a specific extension model
    async requestExtensionSpecificationConfig({commit, state}, configId) {
        commit('startLoading')

        try {
            const resp = await HTTP.get(`sites/${state.site.id}/configuration/product?product_id=${configId}`)
            commit('setExtensionData', { configId: configId, data: resp.data })
            return resp
        } finally {
            commit('stopLoading')
        }
    },

    // request a given site config or create a new one.
    async requestSiteConfig({commit}, siteId) {
        commit('startLoading')

        return HTTP.get(`sites/${siteId}/configuration`)
            .then(resp => {
                console.log(`Did load existing configuration for site`, resp.data)
                let configData = resp.data
                if (resp.data.warning) {
                    commit('addMessage', configData.warning)
                    configData = configData.configuration
                }
                commit('setSiteConfig', configData)
                commit('setGeneratedSiteConfig', configData.data)
                commit('setEEBusConfig', configData.eebus)
            })
            .catch(e => {
                console.log(`No configuration file exists for site`, e.response)
                commit('setSiteConfig', {})
            })
            .finally(commit('stopLoading'))
    },

    // request the config update pending state
    async requestPendingUpdateState({commit}, siteId) {
        commit('startLoading')

        return HTTP.get(`sites/${siteId}/configuration/update_pending`)
            .then(resp => {
                commit('setConfigurationUpdatePending', resp.data)
            })
            .finally(commit('stopLoading'))
    },

    // save complete site settings and generate the config
    async saveAndGenerateSiteConfig({commit, state, getters}, { simulation = false } = {}) {
        commit('resetErrors')
        commit('resetMessages')
        commit('startGenerating')
        commit('setConfigurationUpdatePending', false)
        if (!getters.hasGridMeter) {
            commit('setEmsData', {})
        }
        commit('cleanUpGridMeters')

        const requestPayload = {
            configuration: {
                products: state.plannedProducts,
                residualThings: state.residualThings,
                extensions: state.plannedExtensions,
                ems: state.emsData,
                externalPlantControls: state.externalPlantControls,
                simulated: simulation
            }
        }

        console.log(`Sending configuration to navi for site ${state.site.id}`, requestPayload)

        return HTTP.put(`sites/${state.site.id}/configuration`, requestPayload)
            .then(resp => {
                console.log(`Got generated config for site ${state.site.id}`, resp.data)
                commit('setGeneratedSiteConfig', resp.data.configuration.data)
                commit('confirmSiteConfigChanges')
                return resp.data
            })
            .then( data => {
                if (data.warning) {
                    commit('addMessage', data.warning)
                } else {
                    commit('setGenerationSuccess')
                }

                if (state.redirectUrl) {
                    window.location.replace(state.redirectUrl)
                }
            })
            .catch(e => {
                const details = e.response?.data?.error
                commit('addError', details)
                commit('setGenerationError')
            })
            .finally(() => {
                commit('stopLoading')
                commit('stopGenerating')
            })
    },

    async editProductConfig({dispatch, commit, state}, product) {
      for (const device of product.devices) {
        if (!(device.deviceTemplateId in state.deviceTemplateThings)) {
          await dispatch('requestDeviceSpecificationConfig', {
            deviceTemplateId: device.deviceTemplateId,
            configId: device.configId
          })
        }
      }

      commit('startProductConfig', product)
    },

    async requestAndSetupSite({commit, dispatch, state}, { siteId }) {
        commit('startSiteSetup')
        await dispatch('requestManufacturersAndProductModelVersions')
        await dispatch('requestExtensionModels')
        await dispatch('requestSite', siteId)
        await dispatch('requestSupportedProducts')
        await dispatch('requestSiteConfig', siteId)
        await Promise.all(state.plannedExtensions.map(extension => 
            dispatch('requestExtensionSpecificationConfig', extension.productReferenceUuid)
        ))
        await dispatch('requestPendingUpdateState', siteId)
        commit('stopSiteSetup')
    },

    async applyConfigurationFromTemplate({state}, template) {
        const configuration = getConfigFromTemplate(template)

        const preparedConfig = prepareConfigurationFromTemplate(state, configuration)

        state.plannedProducts = preparedConfig.products
        state.plannedThings = []
        state.emsData = preparedConfig.ems
    }
}

function getConfigFromTemplate(template) {
    switch (template) {
        case 'residential_complex':
            return residentialComplexTemplate;
        case 'residential_household':
            return residentialHouseholdTemplate;
        case 'public_parking':
            return publicParkingTemplate;
        case 'company':
            return companyTemplate;
        case 'industry':
            return industryTemplate;
        default:
            return residentialHouseholdTemplate;
    }
}

function prepareConfigurationFromTemplate(state, config) {
    let newConfig = {}

    newConfig.products = config.products.map(product => {
        product.productModelVersion = state.productModelVersions[product.productModelVersionUuid]
        product.id = uuid()

        const specification = product.productModelVersion
            .device_templates[0].device_specifications.find(ds => ds.id == product.deviceSpecificationUuid)

        const thingsWithIds = product.things.map(thing => {
            return {
                ...thing,
                thingUuid: uuid()
            }
        })

        product.devices = buildDefaultDevices(product.productModelVersion).map(device => {
            return {
                ...device,
                id: uuid(),
                deviceSpecificationId: specification.id,
                deviceSpecificationVersion: specification.version,
                protocolType: specification.protocol_type,
                configId: specification.config_id,
                protocolInformation: product.deviceProtocolInformation,
                things: thingsWithIds
            }
        })

        return added_record(product)
    })

    newConfig.ems = added_record({})

    return newConfig
}

function added_record(record) {
    return {
        ...record,
        _dirty: true,
        _added: true
    }
}
